import { useMemo, useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';

// Visual design picker — sport dropdown + searchable thumbnail grid, single- or
// multi-select. Thumbnails are server-rendered SVGs (by recipe id, neutral team
// data) lazily on scroll + cached in-module so re-opening is instant.

export type PickerRecipe = { id: string; name?: string; sport?: string };

const SAMPLE_LOGO = 'https://storage.googleapis.com/ghostfit-artwork-files/wizard-uploads/1777316233112-Ashwaubenon_Baseball.png';
const thumbCache = new Map<string, string>(); // recipeId -> object URL (small PNG)

// Small rasterized PNG (not the font/logo-embedded SVG, which is ~500KB each).
async function fetchThumb(r: PickerRecipe): Promise<string> {
  const cached = thumbCache.get(r.id);
  if (cached) return cached;
  const res = await fetch(`/api/recipes/${encodeURIComponent(r.id)}/preview`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({
      schoolData: {
        colorway: ['navy', 'gold', 'white'],
        team_text: 'TEAM', mascot_text: 'MASCOT', year: '2026',
        sport: (r.sport || 'SPIRIT').replace(/_/g, ' ').toUpperCase(),
      },
      logoUrl: SAMPLE_LOGO,
      format: 'png',
      outputSize: { width: 256, height: 256 },
    }),
  });
  if (!res.ok) throw new Error(`thumb ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  thumbCache.set(r.id, url);
  return url;
}

export function DesignThumb({ recipe }: { recipe: PickerRecipe }) {
  const ref = useRef<HTMLDivElement>(null);
  const [url, setUrl] = useState<string>(thumbCache.get(recipe.id) || '');
  const [visible, setVisible] = useState(false);

  // Render only when scrolled near the viewport.
  useEffect(() => {
    if (url || visible) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (ents) => { if (ents.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); } },
      { rootMargin: '300px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [url, visible]);

  useEffect(() => {
    if (!visible || url) return;
    let alive = true;
    fetchThumb(recipe).then((u) => { if (alive) setUrl(u); }).catch(() => {});
    return () => { alive = false; };
  }, [visible, recipe, url]);

  return (
    <div ref={ref} className="flex h-full w-full items-center justify-center">
      {url ? <img src={url} alt="" className="h-full w-full object-contain" /> : <span className="text-[10px] text-muted">…</span>}
    </div>
  );
}

export default function DesignPicker({
  recipes,
  value,
  multi = false,
  onChange,
  cols = 2,
  maxHeight = 520,
  defaultSport,
}: {
  recipes: PickerRecipe[];
  value: string[] | string | null;
  multi?: boolean;
  onChange: (v: string[] | string) => void;
  cols?: number;
  maxHeight?: number;
  defaultSport?: string;
}) {
  const [sport, setSport] = useState(defaultSport && defaultSport.length ? defaultSport : 'all');
  const [search, setSearch] = useState('');

  const sports = useMemo(
    () => Array.from(new Set(recipes.map((r) => r.sport).filter(Boolean))).sort() as string[],
    [recipes],
  );
  const countBySport = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of recipes) if (r.sport) m[r.sport] = (m[r.sport] || 0) + 1;
    return m;
  }, [recipes]);

  const selected = useMemo(
    () => new Set(Array.isArray(value) ? value : value ? [value] : []),
    [value],
  );

  const filtered = recipes.filter(
    (r) =>
      (sport === 'all' || r.sport === sport) &&
      (!search.trim() || String(r.name || r.id).toLowerCase().includes(search.toLowerCase())),
  );

  function toggle(id: string) {
    if (multi) {
      const next = new Set(selected);
      if (next.has(id)) next.delete(id); else next.add(id);
      onChange(Array.from(next));
    } else {
      onChange(id);
    }
  }

  return (
    <div>
      <div className="mb-2 flex gap-2">
        <select
          value={sport}
          onChange={(e) => setSport(e.target.value)}
          className="h-8 shrink-0 rounded-sm border border-border bg-white px-2 text-[12px] capitalize"
        >
          <option value="all">All sports ({recipes.length})</option>
          {sports.map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')} ({countBySport[s]})</option>
          ))}
        </select>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="flex-1" />
      </div>

      {multi && (
        <div className="mb-1 text-[11px] text-muted">{selected.size} selected</div>
      )}

      <div
        className="grid gap-2 overflow-y-auto pr-1"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, maxHeight }}
      >
        {filtered.map((r) => {
          const on = selected.has(r.id);
          return (
            <button
              key={r.id}
              onClick={() => toggle(r.id)}
              title={r.name || r.id}
              className={`group relative rounded-md border bg-white p-1 text-left transition ${on ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/40'}`}
            >
              <div
                className="aspect-square w-full overflow-hidden rounded-sm"
                style={{ background: 'repeating-conic-gradient(#f1f1f3 0% 25%, #ffffff 0% 50%) 0 / 14px 14px' }}
              >
                <DesignThumb recipe={r} />
              </div>
              <div className="mt-1 truncate text-[10px] text-text/80">{r.name || r.id}</div>
              {on && (
                <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-white">✓</span>
              )}
            </button>
          );
        })}
        {!filtered.length && (
          <div className="col-span-full p-6 text-center text-[11px] text-muted">No designs match.</div>
        )}
      </div>
    </div>
  );
}
